"use client";

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Shield, Eye, Lock, Users, Globe, Mail, Phone, MapPin, ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const parallaxRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrollPosition = window.scrollY;
        parallaxRef.current.style.transform = `translateY(${scrollPosition * 0.3}px)`;
        parallaxRef.current.style.opacity = 1 - scrollPosition * 0.001;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const topElement = document.getElementById("privacyTop");
    if (topElement) {
      topElement.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const sections = [
    {
      id: "introduction",
      title: t("privacy.sections.introduction.title"),
      icon: <Shield className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.introduction.content.paragraph1"),
        },
        {
          text: t("privacy.sections.introduction.content.paragraph2"),
        },
      ],
    },
    {
      id: "information-collection",
      title: t("privacy.sections.informationCollection.title"),
      icon: <Eye className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.informationCollection.content.text"),
          list: [
            t("privacy.sections.informationCollection.content.list.0"),
            t("privacy.sections.informationCollection.content.list.1"),
            t("privacy.sections.informationCollection.content.list.2"),
            t("privacy.sections.informationCollection.content.list.3"),
            t("privacy.sections.informationCollection.content.list.4"),
            t("privacy.sections.informationCollection.content.list.5"),
          ],
        },
      ],
    },
    {
      id: "how-we-collect",
      title: t("privacy.sections.howWeCollect.title"),
      icon: <Users className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.howWeCollect.content.text"),
          list: [
            t("privacy.sections.howWeCollect.content.list.0"),
            t("privacy.sections.howWeCollect.content.list.1"),
            t("privacy.sections.howWeCollect.content.list.2"),
            t("privacy.sections.howWeCollect.content.list.3"),
          ],
        },
      ],
    },
    {
      id: "information-use",
      title: t("privacy.sections.informationUse.title"),
      icon: <Lock className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.informationUse.content.text"),
          list: [
            t("privacy.sections.informationUse.content.list.0"),
            t("privacy.sections.informationUse.content.list.1"),
            t("privacy.sections.informationUse.content.list.2"),
            t("privacy.sections.informationUse.content.list.3"),
            t("privacy.sections.informationUse.content.list.4"),
          ],
        },
        {
          text: t("privacy.sections.informationUse.content.paragraph2"),
        },
      ],
    },
    {
      id: "information-sharing",
      title: t("privacy.sections.informationSharing.title"),
      icon: <Globe className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.informationSharing.content.paragraph1"),
        },
        {
          text: t("privacy.sections.informationSharing.content.paragraph2"),
        },
      ],
    },
    {
      id: "california-rights",
      title: t("privacy.sections.californiaRights.title"),
      icon: <Shield className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.californiaRights.content.text"),
          list: [
            t("privacy.sections.californiaRights.content.list.0"),
            t("privacy.sections.californiaRights.content.list.1"),
            t("privacy.sections.californiaRights.content.list.2"),
            t("privacy.sections.californiaRights.content.list.3"),
            t("privacy.sections.californiaRights.content.list.4"),
          ],
        },
        {
          text: t("privacy.sections.californiaRights.content.paragraph2"),
        },
      ],
    },
    {
      id: "data-security",
      title: t("privacy.sections.dataSecurity.title"),
      icon: <Lock className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.dataSecurity.content.text"),
        },
      ],
    },
    {
      id: "data-retention",
      title: t("privacy.sections.dataRetention.title"),
      icon: <Shield className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.dataRetention.content.text"),
        },
      ],
    },
    {
      id: "children-privacy",
      title: t("privacy.sections.childrenPrivacy.title"),
      icon: <Users className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.childrenPrivacy.content.text"),
        },
      ],
    },
    {
      id: "international-privacy",
      title: t("privacy.sections.internationalPrivacy.title"),
      icon: <Globe className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.internationalPrivacy.content.text"),
        },
      ],
    },
    {
      id: "policy-updates",
      title: t("privacy.sections.policyUpdates.title"),
      icon: <Shield className="text-[#B82A1E]" size={24} />,
      content: [
        {
          text: t("privacy.sections.policyUpdates.content.text"),
        },
      ],
    },
  ];

  return (
    <div id="privacyTop" className="min-h-screen bg-gradient-to-b from-[#fcf9f5] to-white">
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden flex items-center justify-center">
        <div
          ref={parallaxRef}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "url('/placeholder.svg?height=800&width=1920&text=Privacy+Policy')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-[#7B0000] z-10"></div>

        <div className="relative z-20 text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl font-serif mt-7 mb-4">{t("privacy.hero.title")}</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto font-light">
            {t("privacy.hero.subtitle")}
          </p>
          <div className="mt-8 inline-block">
            <div className="h-0.5 w-20 bg-white/70 mx-auto"></div>
            <div className="h-0.5 w-12 bg-white/50 mx-auto mt-1"></div>
            <div className="h-0.5 w-6 bg-white/30 mx-auto mt-1"></div>
          </div>
        </div>

        <div className="absolute bottom-4 right-4 text-white/20 text-6xl font-serif z-10">ॐ</div>
      </div>

      {/* Navigation */}
      <div className="container mx-auto px-4 py-8">
        <a
          href="/"
          className="inline-flex items-center text-[#B82A1E] hover:text-[#8a1f16] transition-colors duration-300 mb-8"
        >
          <ArrowLeft size={20} className="mr-2" />
          {t("privacy.navigation.backToHome")}
        </a>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Introduction Card */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#B82A1E]/10 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-serif text-[#5D4037] mb-4">
                {t("privacy.introCard.title")}
              </h2>
              <p className="text-[#5D4037]/80 leading-relaxed">
                {t("privacy.introCard.description")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <div className="w-12 h-12 bg-[#B82A1E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="text-[#B82A1E]" size={24} />
                </div>
                <h3 className="font-semibold text-[#5D4037] mb-2">
                  {t("privacy.introCard.features.compliant.title")}
                </h3>
                <p className="text-sm text-[#5D4037]/70">
                  {t("privacy.introCard.features.compliant.description")}
                </p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 bg-[#B82A1E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Eye className="text-[#B82A1E]" size={24} />
                </div>
                <h3 className="font-semibold text-[#5D4037] mb-2">
                  {t("privacy.introCard.features.transparent.title")}
                </h3>
                <p className="text-sm text-[#5D4037]/70">
                  {t("privacy.introCard.features.transparent.description")}
                </p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 bg-[#B82A1E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Lock className="text-[#B82A1E]" size={24} />
                </div>
                <h3 className="font-semibold text-[#5D4037] mb-2">
                  {t("privacy.introCard.features.secure.title")}
                </h3>
                <p className="text-sm text-[#5D4037]/70">
                  {t("privacy.introCard.features.secure.description")}
                </p>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#B82A1E]/10 mb-12">
            <h3 className="text-2xl font-serif text-[#5D4037] mb-6">
              {t("privacy.tableOfContents.title")}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center p-3 rounded-lg hover:bg-[#B82A1E]/5 transition-colors duration-300 group"
                >
                  <div className="mr-3">{section.icon}</div>
                  <span className="text-[#5D4037] group-hover:text-[#B82A1E] transition-colors duration-300">
                    {index + 1}. {section.title}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Policy Sections */}
          {sections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              className="bg-white rounded-2xl p-8 shadow-lg border border-[#B82A1E]/10 mb-8"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-[#B82A1E]/10 rounded-full flex items-center justify-center mr-4">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-serif text-[#5D4037]">{section.title}</h2>
              </div>

              {section.content.map((content, contentIndex) => (
                <div key={contentIndex} className="mb-6 last:mb-0">
                  {content.subtitle && (
                    <h3 className="text-xl font-semibold text-[#5D4037] mb-3">
                      {content.subtitle}
                    </h3>
                  )}

                  {content.text && (
                    <p className="text-[#5D4037]/80 leading-relaxed mb-4">{content.text}</p>
                  )}

                  {content.list && (
                    <ul className="space-y-2 ml-6">
                      {content.list.map((item, itemIndex) => (
                        <li
                          key={itemIndex}
                          className="text-[#5D4037]/80 leading-relaxed flex items-start"
                        >
                          <span className="text-[#B82A1E] mr-2 mt-2">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Contact Information */}
          <div className="bg-gradient-to-br from-[#B82A1E]/5 to-[#B82A1E]/10 rounded-2xl p-8 shadow-lg border border-[#B82A1E]/20">
            <h2 className="text-2xl font-serif text-[#5D4037] mb-6 text-center">
              {t("privacy.contact.title")}
            </h2>
            <p className="text-[#5D4037]/80 leading-relaxed mb-6 text-center">
              {t("privacy.contact.description")}
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#B82A1E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="text-[#B82A1E]" size={24} />
                </div>
                <h3 className="font-semibold text-[#5D4037] mb-2">
                  {t("privacy.contact.email.title")}
                </h3>
                <p className="text-[#5D4037]/70">{t("privacy.contact.email.value")}</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#B82A1E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="text-[#B82A1E]" size={24} />
                </div>
                <h3 className="font-semibold text-[#5D4037] mb-2">
                  {t("privacy.contact.phone.title")}
                </h3>
                <p className="text-[#5D4037]/70">{t("privacy.contact.phone.value")}</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#B82A1E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MapPin className="text-[#B82A1E]" size={24} />
                </div>
                <h3 className="font-semibold text-[#5D4037] mb-2">
                  {t("privacy.contact.address.title")}
                </h3>
                <p className="text-[#5D4037]/70">
                  {t("privacy.contact.address.line1")}
                  <br />
                  {t("privacy.contact.address.line2")}
                </p>
              </div>
            </div>

            <div className="text-center border-t border-[#B82A1E]/20 pt-6">
              <p className="text-[#5D4037]/80 font-semibold mb-2">
                {t("privacy.contact.organization.name")}
              </p>
              <p className="text-sm text-[#5D4037]/70">
                {t("privacy.contact.organization.attention")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
